import { useRef, useMemo } from 'react';

import { EPermissions } from '@/types/permissions.types';
import { useAppSelector } from '@/store/hooks';
import { getUser } from '@/store/slices/user/selectors';
import userHasRoleOnActiveChannel from '../user-has-role-on-active-channel';
import { isEqual } from 'underscore';

type UseHasPermissionProps = {
  allowedRoles?: EPermissions[];
  blockedRoles?: EPermissions[];
};

type UseHasPermissionResult = {
  hasPermission: boolean;
  isLoading: boolean;
};

export default function useHasPermission({
  allowedRoles = [],
  blockedRoles = [],
}: UseHasPermissionProps): UseHasPermissionResult {
  const user = useAppSelector(getUser);

  // Memoize the arrays to prevent unnecessary recalculations
  const memoizedAllowedRoles = useMemo(() => allowedRoles, [allowedRoles]);
  const memoizedBlockedRoles = useMemo(() => blockedRoles, [blockedRoles]);

  const permissionsRef = useRef<null | { allowedRoles: EPermissions[]; blockedRoles: EPermissions[] }>(null);
  const hasPermission = useRef<boolean>(false);

  const permissionsUpdated =
    !isEqual(permissionsRef.current?.allowedRoles, memoizedAllowedRoles) ||
    !isEqual(permissionsRef.current?.blockedRoles, memoizedBlockedRoles);

  if (permissionsUpdated) {
    const defaultAllowRoles = [EPermissions.Owner, EPermissions.Admin, EPermissions.Builder].filter(
      (role) => !memoizedBlockedRoles.includes(role)
    );

    hasPermission.current = userHasRoleOnActiveChannel([...memoizedAllowedRoles, ...defaultAllowRoles], user);
    permissionsRef.current = { allowedRoles: memoizedAllowedRoles, blockedRoles: memoizedBlockedRoles };
  }

  // Since the permission calculation is synchronous, there's no actual loading state
  // isLoading should only be true if we're waiting for the user data to load
  const isLoading = !user;

  return { hasPermission: hasPermission.current, isLoading };
}
